// # models
import { ResourceModel } from "@/models";
// # policies
import { ResourcePolicy } from "@/policies";
// # services
import { auth, notifications } from "@/services";

angular
  .module("app")
  .controller("manageResourceController", manageResourceController);

manageResourceController.$inject = [
  "settings",
  "$scope",
  "$uibModalInstance",
  "mimetypesPayload",
  "alerts",
  "ResourcesApi",
  "UploadsApi",
  "CategoryTypesApi",
  "CategoriesApi",
  "TreeManager",
  "resourceId",
  "confirmDialog",
  "PollsApi",
  "_",
  "GroupsApi",
];

function manageResourceController(
  settings,
  $scope,
  $uibModalInstance,
  mimetypesPayload,
  alerts,
  ResourcesApi,
  UploadsApi,
  CategoryTypesApi,
  CategoriesApi,
  TreeManager,
  resourceId,
  confirmDialog,
  PollsApi,
  _,
  GroupsApi,
) {
  const vm = this;
  const user = auth.user();

  // properties
  vm.permissionChecks = {
    canDelete: ResourcePolicy.canDelete(user),
    canManage: ResourcePolicy.canManage(user),
    canManageStatus: ResourcePolicy.canManageStatus(user),
  };
  vm.isLoading = false;
  vm.isLoadingMessage = "Loading";
  vm.isLoadingProgress = 0;
  vm.createNewResource = !resourceId;
  vm.resource = {
    status: "pending",
    type: "resource",
    uploads: [],
    upload_files: {
      images: [],
      audio: [],
      documents: [],
      videos: [],
    },
    categories: [],
    settings: {},
  };
  vm.categoryTypes = []; // each one with "categories" and "selectedCategoryId" array
  vm.statusOptions = [
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
  ];
  vm.imageUploadOptions = {
    resize: true,
    resizeShape: "rectangle",
    resizeSize: {
      w: 1250,
      h: 640,
    },
    resizeAspectRatio: 0,
  };
  vm.fileUploadOptions = {
    filePattern: "*",
    maxFilesize: settings.maxUploadSize,
    dragOverClass: "dragging",
  };
  vm.resourceImageFile = null;
  vm.mimetypesPayload = mimetypesPayload;

  // methods
  vm.dismiss = $uibModalInstance.dismiss;
  vm.getResource = getResource;
  vm.getCategoryTypes = getCategoryTypes;
  vm.getCategories = getCategories;
  vm.handleBodyChange = handleBodyChange;
  vm.onResourceImageSelected = onResourceImageSelected;
  vm.onResourceImageSelectedError = onResourceImageSelectedError;
  vm.onFileSelected = onFileSelected;
  vm.onFileSelectedError = onFileSelectedError;
  vm.handleSave = handleSave;
  vm.handleDeleteRequest = handleDeleteRequest;
  vm.removeFile = removeFile;
  vm.setCategories = setCategories;
  getGroups();

  /**
   * Initialises the controller.
   *
   * @returns {Promise<void>}
   */
  async function bootstrap(): Promise<void> {
    try {
      vm.isLoading = true;

      if (resourceId) {
        await getResource();
      }

      await getCategoryTypes();
      getPolls();
      vm.isLoading = false;
      $scope.$apply();
    } catch (error) {
      vm.isLoading = false;
      throw error;
    }
  }

  /**
   * Retrieves events
   *
   * @return {void}
   */
  function getGroups() {
    GroupsApi.get({}, (response) => {
      vm.groups = response.data;
    });
  }
  /**
   * Retrieves the resource if editing an existing one
   *
   * @return {Promise<void>}
   */
  function getResource(): Promise<void> {
    return new Promise((resolve, reject) => {
      vm.isLoadingMessage = "Loading resource";

      ResourcesApi.get(
        { id: resourceId },
        (response) => {
          vm.resource = angular.extend(vm.resource, response.data);

          // update permission checks
          const resourceModel = new ResourceModel(vm.resource);
          vm.permissionChecks.canDelete = ResourcePolicy.canDelete(
            user,
            resourceModel,
          );
          vm.permissionChecks.canManage = ResourcePolicy.canManage(
            user,
            resourceModel,
          );

          resolve();
        },
        (error) => {
          notifications.display({
            message: "Could not load resource.",
            theme: "error",
            title: "Error",
          });
          $uibModalInstance.close();
          reject(error);
        },
      );
    });
  }

  /**
   * Retrieves category types from the API
   *
   * @returns {Promise<void>}
   */
  function getCategoryTypes(): Promise<void> {
    return new Promise((resolve, reject) => {
      CategoryTypesApi.query(
        { "categorizables[]": ["resource"] },
        async (response: any) => {
          vm.categoryTypes = response;
          await getCategories();
          resolve();
        },
        reject,
      );
    });
  }

  /**
   * Retrieves categories from the API
   *
   * @returns {Promise<void>}
   */
  function getCategories(): Promise<void> {
    return new Promise((resolve, reject) => {
      CategoriesApi.query((response) => {
        vm.categories = response;
        angular.forEach(vm.categoryTypes, (categoryType) => {
          const cats = vm.categories.filter((cat) => {
            return cat.type_id === categoryType.id;
          });

          categoryType.categories = cats;
        });
        resolve();
      }, reject);
    });
  }

  /**
   * Updates body content.
   *
   * @param {string} content html
   * @returns {void}
   */
  function handleBodyChange(content) {
    vm.resource.body = content;
  }

  /**
   * Caches the selected file
   *
   * @param  {String} file - base64 encoded file
   * @return {Void}
   */
  function onResourceImageSelected(file) {
    vm.resourceImageFile = file;
  }

  function onResourceImageSelectedError(error) {
    alerts.emit({
      theme: "error",
      title: "This file could not be uploaded",
      message: error.image,
    });
  }

  /**
   * Caches the file and uploads to the API
   *
   * @param  {String} base64Data - file converted to base64
   * @param  {Object} file   - brower file object
   * @return {Void}
   */
  function onFileSelected(base64Data, file) {
    const filePlaceholder = {
      client_filename: file.name,
      isUploading: true,
      uploadProgress: 0,
    };

    // check file type is valid
    const acceptedFileTypes = Object.keys(mimetypesPayload);
    if (acceptedFileTypes.indexOf(file.type) === -1) {
      return alerts.emit({
        type: "warning",
        title: "File Error",
        message: "Unsupported file type",
      });
    }

    // determine category file should be placed into
    const fileType = (mimetypesPayload[file.type] || { type: "document" }).type;
    const fileTypeCategoryMap = {
      image: "images",
      audio: "audio",
      document: "documents",
      csv: "documents",
      video: "videos",
    };
    // save reference to the category array that will hold this file
    const fileCategory =
      vm.resource.upload_files[fileTypeCategoryMap[fileType]];

    if (!fileCategory) {
      return alerts.emit({
        theme: "error",
        title: `File type "${fileType}" is not supported`,
      });
    }

    // add file placeholder to resource to update the view
    fileCategory.push(filePlaceholder);

    // get index of file placeholder
    const fileObjectIndex = fileCategory.length - 1;

    // upload the file
    UploadsApi.uploadFile(file, "resource", true).then(
      function handleUploadFileSuccess(response) {
        delete fileCategory[fileObjectIndex].isUploading;
        // save file ID to resource
        vm.resource.uploads.push(response.id);
        // update file placeholder with complete data
        fileCategory[fileObjectIndex] = response;
      },
      function handleUploadFileError() {
        fileCategory.splice(fileObjectIndex, 1);
      },
      function handleUploadProgress(progress) {
        fileCategory[fileObjectIndex].uploadProgress = progress;
      },
    );
  }

  function onFileSelectedError(error) {
    alerts.emit({
      theme: "error",
      title: "This file could not be uploaded",
      message: error.image,
    });
  }

  /**
   * Saves the resource to the API
   *
   * @return {Void}
   */
  function handleSave() {
    vm.isLoading = true;

    // makes put / post request to the API
    function doSave() {
      const method = vm.createNewResource ? "post" : "put";
      vm.isLoadingMessage = "Saving resource";

      // don't modify the original object
      const transformedPost = {
        ...vm.resource,
        upload_files: null,
      };

      if (!vm.permissionChecks.canManage) {
        delete transformedPost.created_at;
      }

      ResourcesApi[method](
        transformedPost,
        (response) => {
          vm.isLoading = false;
          alerts.emit({
            theme: "success",
            title: "Resource saved",
          });
          $uibModalInstance.close(response.data);
        },
        (error) => {
          vm.formErrors = error.data;
          vm.isLoading = false;
        },
      );
    }

    // upload image
    if (vm.resourceImageFile) {
      vm.isLoadingMessage = "Uploading banner image";
      UploadsApi.uploadFile(vm.resourceImageFile, "user").then(
        function handleImageUploadSuccess(response) {
          vm.isLoadingProgress = 0;
          vm.resource.img = response.path;
          doSave();
        },
        function handleImageUploadError() {
          vm.isLoading = false;
          vm.isLoadingProgress = 0;
          alerts.emit({
            theme: "error",
            title: "Banner image could not be uploaded",
          });
        },
        function handleImageUploadProgress(progress) {
          vm.isLoadingProgress = progress;
        },
      );
    } else {
      doSave();
    }
  }

  /**
   * Asks the user for confirmation and sends a DELETE request to the API
   * to remove this resource post
   *
   * @return {Void}
   */
  function handleDeleteRequest() {
    confirmDialog("Are you sure you want to permanently delete this resource?")
      .result.then((canDelete) => {
        if (!canDelete) {
          return false;
        }

        vm.isLoading = true;
        vm.isLoadingMessage = "Deleting";
        ResourcesApi.delete(
          vm.resource,
          function handleDeleteResourceSuccess() {
            alerts.emit({
              theme: "success",
              title: "Resource deleted",
            });
            vm.resource.deleted = true;
            $uibModalInstance.close(vm.resource);
          },
          function handleDeleteResourceError() {
            vm.isLoading = false;
          },
        );
      })
      .catch(angular.noop);
  }

  /**
   * Removes a file from the (local) resource.
   *
   * @param {object} file
   * @returns {void}
   */
  function removeFile(file): void {
    const remove = () => {
      // Filter the deleted file out of the "uploads" ID array
      vm.resource.uploads = vm.resource.uploads.filter(
        (uploadId) => uploadId !== file.id,
      );

      // Filter the deleted file out of the "upload_files" object of categories
      Object.keys(vm.resource.upload_files).forEach((key) => {
        vm.resource.upload_files[key] = vm.resource.upload_files[key].filter(
          (upload) => upload.id !== file.id,
        );
      });
    };

    confirmDialog("Are you sure you want to remove this file?").result.then(
      (canRemove) => canRemove && remove(),
    );
  }

  /**
   * Replaces selected categories on the resources object.
   *
   * @param {number[]} categoryIds
   * @returns {void}
   */
  function setCategories(categoryIds: number[]): void {
    vm.resource.categories = categoryIds;
    $scope.$apply();
  }

  /**
   * Retrieves polls
   *
   * @return {void}
   */
  function getPolls() {
    PollsApi.get(
      {
        status: "approved",
      },
      (response) => {
        vm.polls = response.data;
      },
    );
  }

  bootstrap();
}
